import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle,
  X,
  Warning,
  PaperPlaneTilt,
  Printer,
  Clock,
  FileText,
  ArrowRight,
  Prohibit,
} from "@phosphor-icons/react";
import { useTeamRoles } from "./Requisitions";

/**
 * Requisition Detail — the shared conversation between Production and Store.
 *
 * The action bar adapts to (a) the requisition's status and (b) the viewer's
 * role. Backend endpoints enforce the same rules, so hiding a button here is
 * purely cosmetic (defence-in-depth).
 */
const STATUS_BANNER = {
  draft: {
    tone: "bg-slate-50 border-slate-200 text-slate-700",
    heading: "Draft — not yet visible to Store",
    body: "Submit when you're ready. Once submitted the stock snapshot is refreshed for Store's review.",
  },
  submitted: {
    tone: "bg-amber-50 border-amber-300 text-amber-900",
    heading: "Awaiting Store team approval",
    body: "Store team can approve to auto-reserve stock, or reject with a reason.",
  },
  approved: {
    tone: "bg-emerald-50 border-emerald-300 text-emerald-900",
    heading: "Approved — materials reserved",
    body: "Production Run created and is ready to issue on the shop floor.",
  },
  rejected: {
    tone: "bg-rose-50 border-rose-300 text-rose-900",
    heading: "Rejected by Store",
    body: "No stock was touched. Create a new requisition once the concern is addressed.",
  },
  cancelled: {
    tone: "bg-slate-100 border-slate-300 text-slate-600",
    heading: "Cancelled",
    body: "This requisition was withdrawn and no stock was reserved.",
  },
};

export default function RequisitionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canCreate, canApprove } = useTeamRoles();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/inventory/requisitions/${id}`);
      setDoc(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Requisition not found");
      navigate("/inventory/requisitions");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/requisitions/${id}/submit`);
      setDoc(data);
      toast.success(`${data.code} submitted to Store team`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this requisition? Nothing has been reserved yet.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/requisitions/${id}/cancel`, {
        reason: "Cancelled by requester from detail view",
      });
      setDoc(data);
      toast.success("Requisition cancelled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel");
    } finally {
      setBusy(false);
    }
  };

  const printStoreCopy = () => {
    // Open the HTML store copy in a new tab; users hit Ctrl/Cmd-P to save PDF.
    const base = process.env.REACT_APP_BACKEND_URL || "";
    window.open(`${base}/api/inventory/requisitions/${id}/pdf`, "_blank", "noopener");
  };

  if (loading || !doc) {
    return <div className="text-sm text-slate-500 p-6" data-testid="req-detail-loading">Loading requisition…</div>;
  }

  const banner = STATUS_BANNER[doc.status] || STATUS_BANNER.draft;
  const shortage = Number(doc.total_shortage_at_request || 0);
  const isOwner = doc.requested_by_email; // strict ownership is enforced backend-side; UI shows the button and lets backend reject
  const canSubmit = doc.status === "draft" && canCreate;
  const canCancel = ["draft", "submitted"].includes(doc.status) && canCreate;
  const canApproveOrReject = doc.status === "submitted" && canApprove;

  return (
    <div className="space-y-5" data-testid="req-detail-page">
      <div>
        <Link
          to="/inventory/requisitions"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
          data-testid="req-detail-back"
        >
          <ArrowLeft size={12} weight="bold" /> Back to Requisitions
        </Link>
      </div>

      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-mono text-2xl font-bold text-slate-900">{doc.code}</h1>
            <span className="text-sm text-slate-500">·</span>
            <div className="font-heading text-lg font-semibold text-slate-800">{doc.recipe_name}</div>
            <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400">v{doc.recipe_version}</span>
          </div>
          <div className="text-sm text-slate-500 mt-1">
            Requested by <span className="font-medium text-slate-700">{doc.requested_by_name || doc.requested_by_email}</span>
            &nbsp;· {(doc.requested_at || "").slice(0, 10)}
            {doc.target_production_date && <> &nbsp;· Target production {doc.target_production_date}</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={printStoreCopy}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-white transition"
            data-testid="req-print-store-copy"
            title="Open printable Store Copy in a new tab"
          >
            <Printer size={12} weight="bold" /> Store Copy
          </button>
        </div>
      </header>

      <div className={`border rounded-lg px-4 py-3 ${banner.tone}`} data-testid={`req-status-banner-${doc.status}`}>
        <div className="text-sm font-semibold">{banner.heading}</div>
        <div className="text-xs mt-1 opacity-90">{banner.body}</div>
        {doc.status === "rejected" && doc.rejection_reason && (
          <div className="mt-2 text-xs bg-white/60 rounded px-2.5 py-1.5 italic border border-rose-200">
            <strong>Reason:</strong> {doc.rejection_reason}
          </div>
        )}
        {doc.status === "approved" && doc.linked_run_code && (
          <div className="mt-3 flex items-center gap-3">
            <Link
              to={`/inventory/production-runs/${doc.linked_run_id}`}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
              data-testid="req-open-run-link"
            >
              Open {doc.linked_run_code} <ArrowRight size={12} weight="bold" />
            </Link>
            {doc.approval_notes && (
              <span className="text-xs italic text-emerald-800">&ldquo;{doc.approval_notes}&rdquo;</span>
            )}
          </div>
        )}
      </div>

      {/* Meta grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetaCard label="Batch Size" value={`${doc.batch_size} kg`} testId="req-meta-batch-size" />
        <MetaCard label="Expected FG Yield" value={`${doc.expected_fg_yield} kg`} testId="req-meta-fg-yield" />
        <MetaCard label="Finished Good" value={doc.fg_item_name || "—"} testId="req-meta-fg-item" />
        <MetaCard
          label="Total Material Cost"
          value={`₹${(doc.total_material_cost || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
          testId="req-meta-cost"
        />
      </div>

      {/* Materials table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">Bill of Materials</div>
            <div className="text-sm text-slate-700 mt-0.5">
              {doc.materials?.length || 0} ingredients · snapshot taken at {(doc.updated_at || "").slice(0, 16).replace("T", " ")}
            </div>
          </div>
          {shortage > 0 ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold">
              <Warning size={12} weight="fill" /> Shortage {shortage.toFixed(3)}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
              <CheckCircle size={12} weight="fill" /> All materials available
            </span>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-slate-500 font-bold">
              <th className="px-4 py-2.5">#</th>
              <th className="px-4 py-2.5">Section</th>
              <th className="px-4 py-2.5">Brand</th>
              <th className="px-4 py-2.5">Ingredient</th>
              <th className="px-4 py-2.5 text-right">Required</th>
              <th className="px-4 py-2.5 text-right">In Stock</th>
              <th className="px-4 py-2.5 text-right">Shortage</th>
            </tr>
          </thead>
          <tbody>
            {(doc.materials || []).map((m, i) => {
              const short = Number(m.shortage_at_request || 0);
              return (
                <tr
                  key={m.item_id || `${m.ingredient_name}-${i}`}
                  className={`border-b border-slate-50 last:border-0 ${short > 0 ? "bg-rose-50/40" : ""}`}
                  data-testid={`req-mat-row-${i}`}
                >
                  <td className="px-4 py-2.5 text-slate-400 text-xs">{i + 1}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">{m.section || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">{m.brand || "—"}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900">{m.ingredient_name}</div>
                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">{m.item_code || m.item_id}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-900 tabular-nums">
                    {(m.required || 0).toFixed(3)} {m.uom}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-700 tabular-nums">
                    {(m.available_at_request || 0).toFixed(3)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {short > 0 ? (
                      <span className="text-rose-700 font-bold">{short.toFixed(3)}</span>
                    ) : (
                      <span className="text-emerald-600 text-xs font-medium">OK</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {doc.notes && (
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-md px-4 py-3" data-testid="req-notes-block">
          <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-amber-800 mb-1">Production Notes</div>
          <div className="text-sm text-amber-900">{doc.notes}</div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-2 flex-wrap justify-end border-t border-slate-200 pt-4">
        {canCancel && (
          <button
            onClick={cancel}
            disabled={busy}
            className="h-10 px-4 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="req-cancel-btn"
          >
            <Prohibit size={12} weight="bold" className="inline mr-1.5" /> Cancel Requisition
          </button>
        )}
        {canSubmit && (
          <button
            onClick={submit}
            disabled={busy}
            className="h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 shadow-sm inline-flex items-center gap-2"
            data-testid="req-submit-btn"
          >
            <PaperPlaneTilt size={14} weight="bold" /> Submit to Store
          </button>
        )}
        {canApproveOrReject && (
          <>
            <button
              onClick={() => setRejectOpen(true)}
              disabled={busy}
              className="h-10 px-4 rounded-md border border-rose-300 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="req-reject-btn"
            >
              <X size={12} weight="bold" /> Reject
            </button>
            <button
              onClick={() => setApproveOpen(true)}
              disabled={busy || shortage > 0}
              title={shortage > 0 ? "Cannot approve while any material is short" : ""}
              className="h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm inline-flex items-center gap-2"
              data-testid="req-approve-btn"
            >
              <CheckCircle size={14} weight="bold" /> Approve & Reserve Stock
            </button>
          </>
        )}
        {!canSubmit && !canCancel && !canApproveOrReject && (
          <div className="text-xs text-slate-400 italic">
            No further actions available at this status.
          </div>
        )}
      </div>

      {rejectOpen && (
        <RejectModal id={id} onClose={() => setRejectOpen(false)} onDone={(d) => { setDoc(d); setRejectOpen(false); }} />
      )}
      {approveOpen && (
        <ApproveModal id={id} onClose={() => setApproveOpen(false)} onDone={(d) => {
          setApproveOpen(false);
          setDoc(d.requisition || d);
          toast.success(`Approved — Production Run ${d.production_run?.code} is ready to issue`);
        }} />
      )}
    </div>
  );
}

function MetaCard({ label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md px-4 py-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function RejectModal({ id, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!reason.trim()) { toast.error("Rejection reason is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/requisitions/${id}/reject`, { rejection_reason: reason.trim() });
      onDone(data);
      toast.success("Rejection recorded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reject");
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Reject Requisition" tone="rose" onClose={onClose} testId="req-reject-modal">
      <p className="text-sm text-slate-600 mb-3">
        Explain to the Production team why this requisition can&apos;t be fulfilled. This will be recorded in the audit log.
      </p>
      <textarea
        rows={4}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Batch of turmeric expired · Awaiting GRN acceptance · Priority order in progress…"
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500"
        data-testid="req-reject-reason"
        autoFocus
      />
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="h-9 px-4 rounded-md border border-slate-300 text-sm font-medium text-slate-700">Never mind</button>
        <button
          onClick={go}
          disabled={busy || !reason.trim()}
          className="h-9 px-4 rounded-md bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50"
          data-testid="req-reject-confirm"
        >
          {busy ? "Rejecting…" : "Reject"}
        </button>
      </div>
    </Modal>
  );
}

function ApproveModal({ id, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/requisitions/${id}/approve`, { approval_notes: notes.trim() });
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to approve");
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Approve & Reserve Stock" tone="emerald" onClose={onClose} testId="req-approve-modal">
      <p className="text-sm text-slate-600 mb-3">
        This will lock the required stock (FIFO/FEFO) and open a fresh Production Run in <strong>reserved</strong> state. The Production team can then issue materials on the shop floor.
      </p>
      <label className="block text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1.5">Approval notes (optional)</label>
      <textarea
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="e.g. Use pouches from bay-3 · Prioritise closer-expiry lots…"
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
        data-testid="req-approve-notes"
      />
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="h-9 px-4 rounded-md border border-slate-300 text-sm font-medium text-slate-700">Cancel</button>
        <button
          onClick={go}
          disabled={busy}
          className="h-9 px-4 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
          data-testid="req-approve-confirm"
        >
          {busy ? "Approving…" : "Approve & Reserve"}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children, tone = "emerald", testId }) {
  const toneClass = tone === "rose" ? "text-rose-700" : "text-emerald-700";
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid={testId}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className={`font-heading text-base font-bold ${toneClass}`}>{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} weight="bold" /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}
